package com.checkin.host.ui.contracts

import androidx.compose.runtime.*
import com.checkin.host.api.ApiClient
import com.checkin.host.api.ApiException
import com.checkin.host.theme.AppColors
import com.checkin.host.theme.cardStyle
import com.checkin.host.theme.primaryButton
import com.checkin.host.theme.secondaryButton
import com.checkin.host.ui.components.TextField
import com.checkin.host.ui.layout.AlertBanner
import com.checkin.host.ui.layout.PageHeader
import com.checkin.host.ui.layout.rememberIsMobile
import com.checkin.host.ui.properties.Badge
import com.checkin.host.util.arrayBufferToByteArray
import com.checkin.host.util.openPdfInNewTab
import com.checkin.host.util.pngBytesToObjectUrl
import com.checkin.host.util.revokeObjectUrl
import com.checkin.shared.dto.ContractTemplateDto
import com.checkin.shared.dto.UpdateProfileRequest
import com.checkin.shared.dto.UserDto
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.ref
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.Event
import org.w3c.files.FileReader

@Composable
fun ContractsScreen(user: UserDto, onUserUpdated: (UserDto) -> Unit) {
    var contractLandlordName by remember(user) {
        mutableStateOf(user.contractLandlordName ?: user.displayName)
    }
    var message by remember { mutableStateOf<String?>(null) }
    var isError by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(false) }
    var canvasRef by remember { mutableStateOf<HTMLCanvasElement?>(null) }
    var signatureDirty by remember { mutableStateOf(false) }
    var clearSignature by remember { mutableStateOf(false) }
    var signatureSeed by remember(user.id, user.hasHostSignature) { mutableStateOf<ByteArray?>(null) }
    var logoSeed by remember(user.id, user.hasHostLogo) { mutableStateOf<ByteArray?>(null) }
    var stampSeed by remember(user.id, user.hasHostStamp) { mutableStateOf<ByteArray?>(null) }
    var logoPending by remember { mutableStateOf<String?>(null) }
    var stampPending by remember { mutableStateOf<String?>(null) }
    var clearLogo by remember { mutableStateOf(false) }
    var clearStamp by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val isMobile = rememberIsMobile()

    LaunchedEffect(user.id, user.hasHostSignature, user.hasHostLogo, user.hasHostStamp) {
        signatureSeed = if (user.hasHostSignature) runCatching { ApiClient.hostSignature() }.getOrNull() else null
        logoSeed = if (user.hasHostLogo) runCatching { ApiClient.hostLogo() }.getOrNull() else null
        stampSeed = if (user.hasHostStamp) runCatching { ApiClient.hostStamp() }.getOrNull() else null
        signatureDirty = false
        clearSignature = false
        logoPending = null
        stampPending = null
        clearLogo = false
        clearStamp = false
    }

    PageHeader("Contrats", "Bailleur, logo et corps du contrat")

    message?.let { AlertBanner(it, isError) }

    Div({
        style {
            cardStyle()
            property("max-width", if (isMobile) "100%" else "640px")
            marginBottom(24.px)
        }
    }) {
        H3({ style { marginTop(0.px) } }) { Text("Identité du bailleur") }
        P({ style { color(AppColors.muted); fontSize(14.px); margin(0.px, 0.px, 16.px, 0.px) } }) {
            Text("Nom, logo, signature et cachet affichés sur le contrat signé par vos invités.")
        }
        Div({ style { marginBottom(12.px) } }) {
            TextField("Nom du bailleur sur le contrat", contractLandlordName) { contractLandlordName = it }
        }
        BrandImageField(
            label = "Logo (en-tête du contrat)",
            seedBytes = logoSeed,
            pendingBase64 = logoPending,
            cleared = clearLogo,
            onPickBase64 = { logoPending = it; clearLogo = false },
            onClear = { logoPending = null; clearLogo = true },
        )
        P({ style { fontSize(13.px); color(AppColors.muted); margin(12.px, 0.px, 8.px, 0.px) } }) {
            Text("Signature du bailleur")
        }
        key(signatureSeed?.contentHashCode() ?: user.hasHostSignature.hashCode()) {
            HostSignatureCanvas(
                onCanvasRef = { canvasRef = it },
                signatureSeed = signatureSeed,
                onSignatureChanged = {
                    signatureDirty = true
                    clearSignature = false
                },
            )
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                margin(12.px, 0.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            Button({
                style { secondaryButton(); fontSize(13.px) }
                onClick {
                    canvasRef?.let { resetSignatureCanvas(it) }
                    signatureDirty = true
                    clearSignature = true
                }
            }) { Text("Effacer la signature") }
        }
        BrandImageField(
            label = "Cachet / tampon (optionnel)",
            seedBytes = stampSeed,
            pendingBase64 = stampPending,
            cleared = clearStamp,
            onPickBase64 = { stampPending = it; clearStamp = false },
            onClear = { stampPending = null; clearStamp = true },
        )
        Button({
            style { primaryButton(); marginTop(12.px) }
            onClick {
                scope.launch {
                    loading = true
                    message = null
                    try {
                        val signaturePayload = when {
                            clearSignature -> ""
                            signatureDirty -> {
                                val canvas = canvasRef ?: return@launch
                                canvas.toDataURL("image/png").substringAfter(",")
                            }
                            else -> null
                        }
                        val logoPayload = when {
                            clearLogo -> ""
                            logoPending != null -> logoPending
                            else -> null
                        }
                        val stampPayload = when {
                            clearStamp -> ""
                            stampPending != null -> stampPending
                            else -> null
                        }
                        val updated = ApiClient.updateProfile(
                            UpdateProfileRequest(
                                contractLandlordName = contractLandlordName.trim(),
                                hostSignatureBase64 = signaturePayload,
                                hostLogoBase64 = logoPayload,
                                hostStampBase64 = stampPayload,
                            ),
                        )
                        ApiClient.saveUser(updated)
                        onUserUpdated(updated)
                        signatureDirty = false
                        clearSignature = false
                        logoPending = null
                        stampPending = null
                        clearLogo = false
                        clearStamp = false
                        message = "Identité bailleur enregistrée"
                        isError = false
                    } catch (e: ApiException) {
                        message = e.message
                        isError = true
                    } finally {
                        loading = false
                    }
                }
            }
        }) { Text(if (loading) "..." else "Enregistrer l'identité") }
    }

    ContractBodySection()
}

@Composable
private fun HostSignatureCanvas(
    onCanvasRef: (HTMLCanvasElement?) -> Unit,
    signatureSeed: ByteArray?,
    onSignatureChanged: () -> Unit,
) {
    Canvas(attrs = {
        ref { element ->
            onCanvasRef(element)
            element.width = 400
            element.height = 120
            val ctx = element.getContext("2d") as CanvasRenderingContext2D
            var isDrawing = false

            fun resetCanvas() {
                ctx.fillStyle = "#ffffff"
                ctx.fillRect(0.0, 0.0, 400.0, 120.0)
                ctx.strokeStyle = "#d1d5db"
                ctx.lineWidth = 1.0
                ctx.strokeRect(0.5, 0.5, 399.0, 119.0)
            }

            fun canvasPoint(clientX: Double, clientY: Double): Pair<Double, Double> {
                val rect = element.getBoundingClientRect()
                val scaleX = element.width.toDouble() / rect.width
                val scaleY = element.height.toDouble() / rect.height
                return (clientX - rect.left) * scaleX to (clientY - rect.top) * scaleY
            }

            fun startStroke(clientX: Double, clientY: Double) {
                isDrawing = true
                val (x, y) = canvasPoint(clientX, clientY)
                ctx.strokeStyle = "#1f2937"
                ctx.lineWidth = 2.0
                ctx.asDynamic().lineCap = "round"
                ctx.asDynamic().lineJoin = "round"
                ctx.beginPath()
                ctx.moveTo(x, y)
            }

            fun continueStroke(clientX: Double, clientY: Double) {
                if (!isDrawing) return
                val (x, y) = canvasPoint(clientX, clientY)
                ctx.lineTo(x, y)
                ctx.stroke()
            }

            fun endStroke() {
                if (isDrawing) {
                    isDrawing = false
                    onSignatureChanged()
                }
            }

            resetCanvas()
            signatureSeed?.let { bytes ->
                val img = document.createElement("img") as HTMLImageElement
                val url = pngBytesToObjectUrl(bytes)
                img.onload = {
                    ctx.drawImage(img, 0.0, 0.0, 400.0, 120.0)
                    revokeObjectUrl(url)
                }
                img.src = url
            }

            element.onmousedown = { event ->
                event.preventDefault()
                startStroke(event.clientX.toDouble(), event.clientY.toDouble())
            }
            element.onmousemove = { event ->
                if (isDrawing) {
                    event.preventDefault()
                    continueStroke(event.clientX.toDouble(), event.clientY.toDouble())
                }
            }
            element.onmouseup = { endStroke() }
            element.onmouseleave = { endStroke() }

            element.asDynamic().ontouchstart = { event: Event ->
                val e = event.asDynamic()
                e.preventDefault()
                val touch = e.touches.item(0)
                if (touch != null) {
                    startStroke(touch.clientX as Double, touch.clientY as Double)
                }
            }
            element.asDynamic().ontouchmove = { event: Event ->
                val e = event.asDynamic()
                e.preventDefault()
                val touch = e.touches.item(0)
                if (touch != null) {
                    continueStroke(touch.clientX as Double, touch.clientY as Double)
                }
            }
            element.asDynamic().ontouchend = { event: Event ->
                event.asDynamic().preventDefault()
                endStroke()
            }
            element.asDynamic().ontouchcancel = { endStroke() }

            onDispose {
                onCanvasRef(null)
                element.onmousedown = null
                element.onmousemove = null
                element.onmouseup = null
                element.onmouseleave = null
                element.asDynamic().ontouchstart = null
                element.asDynamic().ontouchmove = null
                element.asDynamic().ontouchend = null
                element.asDynamic().ontouchcancel = null
            }
        }
        style {
            width(100.percent)
            height(120.px)
            borderRadius(8.px)
            property("touch-action", "none")
            cursor("crosshair")
        }
    })
}

private fun resetSignatureCanvas(element: HTMLCanvasElement) {
    val ctx = element.getContext("2d") as CanvasRenderingContext2D
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0.0, 0.0, 400.0, 120.0)
    ctx.strokeStyle = "#d1d5db"
    ctx.lineWidth = 1.0
    ctx.strokeRect(0.5, 0.5, 399.0, 119.0)
}

@Composable
private fun BrandImageField(
    label: String,
    seedBytes: ByteArray?,
    pendingBase64: String?,
    cleared: Boolean,
    onPickBase64: (String) -> Unit,
    onClear: () -> Unit,
) {
    var fileInputRef by remember { mutableStateOf<HTMLInputElement?>(null) }
    val previewUrl = remember(seedBytes, pendingBase64, cleared) {
        when {
            cleared -> null
            pendingBase64 != null -> "data:image/png;base64,$pendingBase64"
            seedBytes != null -> pngBytesToObjectUrl(seedBytes)
            else -> null
        }
    }

    DisposableEffect(previewUrl) {
        onDispose {
            if (previewUrl != null && previewUrl.startsWith("blob:")) {
                revokeObjectUrl(previewUrl)
            }
        }
    }

    P({ style { fontSize(13.px); color(AppColors.muted); margin(12.px, 0.px, 8.px, 0.px) } }) {
        Text(label)
    }
    previewUrl?.let { url ->
        Div({
            style {
                marginBottom(8.px)
                padding(8.px)
                border(1.px, LineStyle.Solid, Color("#e5e7eb"))
                borderRadius(8.px)
                backgroundColor(Color.white)
            }
        }) {
            Img(src = url, attrs = { style { maxHeight(64.px); maxWidth(100.percent) } })
        }
    }
    Div({ style { display(DisplayStyle.Flex); gap(8.px); flexWrap(FlexWrap.Wrap) } }) {
        Input(InputType.File, attrs = {
            attr("accept", "image/png,image/jpeg,image/webp")
            style { display(DisplayStyle.None) }
            ref { element ->
                fileInputRef = element
                element.onchange = {
                    element.files?.item(0)?.let { file ->
                        val reader = FileReader()
                        reader.onload = { _: dynamic ->
                            val buffer = reader.result as org.khronos.webgl.ArrayBuffer
                            val bytes = arrayBufferToByteArray(buffer)
                            onPickBase64(bytes.toBase64())
                        }
                        reader.readAsArrayBuffer(file)
                    }
                }
                onDispose {
                    element.onchange = null
                    fileInputRef = null
                }
            }
        })
        Button({ style { secondaryButton(); fontSize(13.px) }; onClick { fileInputRef?.click() } }) {
            Text("Choisir une image")
        }
        if (previewUrl != null) {
            Button({ style { secondaryButton(); fontSize(13.px) }; onClick { onClear() } }) {
                Text("Supprimer")
            }
        }
    }
}

private fun ByteArray.toBase64(): String {
    val chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/"
    var result = ""
    var i = 0
    while (i < size) {
        val b0 = this[i++].toInt() and 0xFF
        val b1 = if (i < size) this[i++].toInt() and 0xFF else -1
        val b2 = if (i < size) this[i++].toInt() and 0xFF else -1
        val triple = (b0 shl 16) or ((if (b1 >= 0) b1 else 0) shl 8) or (if (b2 >= 0) b2 else 0)
        result += chars[(triple shr 18) and 0x3F]
        result += chars[(triple shr 12) and 0x3F]
        result += if (b1 >= 0) chars[(triple shr 6) and 0x3F] else "="
        result += if (b2 >= 0) chars[triple and 0x3F] else "="
    }
    return result
}

@Composable
private fun ContractBodySection() {
    var templates by remember { mutableStateOf<List<ContractTemplateDto>>(emptyList()) }
    var bodyText by remember { mutableStateOf("") }
    var templateName by remember { mutableStateOf("Mon contrat") }
    var userTemplateId by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var success by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val isMobile = rememberIsMobile()

    fun load() {
        scope.launch {
            loading = true
            error = null
            try {
                templates = ApiClient.listContractTemplates()
                val userTemplate = templates.firstOrNull { !it.isSystemDefault }
                val systemTemplate = templates.firstOrNull { it.isSystemDefault }
                if (userTemplate != null) {
                    userTemplateId = userTemplate.id
                    templateName = userTemplate.name
                    bodyText = userTemplate.bodyText
                } else {
                    userTemplateId = null
                    templateName = "Mon contrat"
                    bodyText = systemTemplate?.bodyText.orEmpty()
                }
            } catch (e: ApiException) {
                error = e.message
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { load() }

    val previewTemplateId = userTemplateId ?: templates.firstOrNull { it.isSystemDefault }?.id

    Div({
        style {
            cardStyle()
            property("max-width", if (isMobile) "100%" else "640px")
        }
    }) {
        H3({ style { marginTop(0.px) } }) { Text("Corps du contrat") }
        P({ style { color(AppColors.muted); fontSize(14.px) } }) {
            Text(
                "Rédigez vos clauses (à partir de la destination des lieux). " +
                    "L'adresse du bien, la durée et les informations des parties sont ajoutées automatiquement au PDF.",
            )
        }

        error?.let { AlertBanner(it, isError = true) }
        success?.let { AlertBanner(it, isError = false) }

        if (loading) {
            P({ style { color(AppColors.muted) } }) { Text("Chargement...") }
        } else {
            Div({ style { marginBottom(8.px) } }) {
                TextField("Nom du modèle", templateName) { templateName = it }
            }
            TextArea(value = bodyText, attrs = {
                style {
                    width(100.percent)
                    minHeight(280.px)
                    padding(12.px)
                    borderRadius(8.px)
                    border(1.px, LineStyle.Solid, Color("#e5e7eb"))
                    fontSize(14.px)
                    property("resize", "vertical")
                    property("box-sizing", "border-box")
                    property("font-family", "inherit")
                }
                ref { element ->
                    element.oninput = { event ->
                        bodyText = (event.target as HTMLTextAreaElement).value
                    }
                    onDispose { element.oninput = null }
                }
            })
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    marginTop(12.px)
                    flexWrap(FlexWrap.Wrap)
                    if (isMobile) flexDirection(FlexDirection.Column)
                }
            }) {
                Button({
                    style { primaryButton(); if (isMobile) width(100.percent) }
                    onClick {
                        if (bodyText.isBlank()) {
                            error = "Le corps du contrat ne peut pas être vide"
                            return@onClick
                        }
                        scope.launch {
                            saving = true
                            error = null
                            success = null
                            try {
                                if (userTemplateId != null) {
                                    ApiClient.updateContractTemplate(
                                        userTemplateId!!,
                                        name = templateName.trim(),
                                        bodyText = bodyText.trim(),
                                    )
                                } else {
                                    val created = ApiClient.createContractTemplate(
                                        templateName.trim(),
                                        bodyText.trim(),
                                    )
                                    userTemplateId = created.id
                                }
                                success = "Contrat enregistré"
                                load()
                            } catch (e: ApiException) {
                                error = e.message
                            } finally {
                                saving = false
                            }
                        }
                    }
                }) { Text(if (saving) "..." else "Enregistrer le contrat") }

                previewTemplateId?.let { id ->
                    Button({
                        style { secondaryButton(); if (isMobile) width(100.percent) }
                        onClick {
                            scope.launch {
                                try {
                                    val bytes = ApiClient.downloadContractPreview(id)
                                    openPdfInNewTab(bytes)
                                } catch (e: ApiException) {
                                    error = e.message
                                }
                            }
                        }
                    }) { Text("Aperçu PDF") }
                }

                userTemplateId?.let { id ->
                    Button({
                        style {
                            color(AppColors.danger)
                            backgroundColor(Color.transparent)
                            border(0.px)
                            cursor("pointer")
                            if (isMobile) width(100.percent)
                        }
                        onClick {
                            scope.launch {
                                try {
                                    ApiClient.deleteContractTemplate(id)
                                    success = "Modèle supprimé — modèle système utilisé"
                                    load()
                                } catch (e: ApiException) {
                                    error = e.message
                                }
                            }
                        }
                    }) { Text("Revenir au modèle système") }
                }
            }

            templates.firstOrNull { it.isSystemDefault }?.let {
                Div({ style { marginTop(12.px) } }) {
                    Badge("Modèle SmartCheckin", AppColors.infoBg, AppColors.info)
                }
            }
        }
    }
}
